import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Divider,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/components/common';
import {C71VO, formatAmount, formatPct} from '@/services/auditService/materialityService';

interface HeaderFieldProps {
    label: string;
    value?: string;
}

const HeaderField: React.FC<HeaderFieldProps> = ({label, value}) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
        <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap', minWidth: 90}}>
            {label}
        </Typography>
        <Box sx={{flex: 1}}>
            <InputField type="text" fullWidth compact align="left" defaultValue={value}/>
        </Box>
    </Stack>
);

const MaterialityRow: React.FC<{
    label: string;
    initPct?: number;
    initResult?: number;
    initLevel?: number;
    finPct?: number;
    finResult?: number;
    finLevel?: number;
}> = ({label, initPct, initResult, initLevel, finPct, finResult, finLevel}) => (
    <TableRow>
        <TableCell>{label}</TableCell>
        <TableCell align="right">{formatPct(initPct)}</TableCell>
        <TableCell align="right">{formatAmount(initResult)}</TableCell>
        <TableCell align="right">{formatAmount(initLevel)}</TableCell>
        <TableCell align="right">{formatPct(finPct)}</TableCell>
        <TableCell align="right">{formatAmount(finResult)}</TableCell>
        <TableCell align="right">{formatAmount(finLevel)}</TableCell>
    </TableRow>
);

export interface C71DeterminingMaterialityProps {
    data: C71VO;
}

const C71DeterminingMateriality: React.FC<C71DeterminingMaterialityProps> = ({data}) => (
    <Box>
        <Card sx={{mb: 2}}>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 4}}>
                        <HeaderField label="Client:" value={data.client}/>
                        <HeaderField label="Year end:" value={data.yearEnd}/>
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <HeaderField label="Prepared by:" value={data.preparedBy}/>
                        <HeaderField label="Reviewed by:" value={data.reviewedBy}/>
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <HeaderField label="Ref:" value="C7.1"/>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>

        <Typography variant="h6" fontWeight={600} gutterBottom>
            DETERMINING MATERIALITY
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            The determination of what is material is a matter of professional judgement. The percentage
            benchmarks set out below are intended to provide guidance in exercising that judgement.
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} sx={{mb: 1}}>
            Materiality ranges
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{mb: 3, maxWidth: 720}}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Range of turnover or gross assets</TableCell>
                        <TableCell align="right">Percentage of turnover or gross assets</TableCell>
                        <TableCell align="right">Materiality ranges</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[
                        ['HK$0 to HK$7.5 m', '3%', 'HK$1 - HK$225k'],
                        ['HK$7.5 m to HK$15 m', '2.5%', 'HK$225k - HK$375k'],
                        ['HK$15 m to HK$30 m', '2%', 'HK$375k - HK$600k'],
                        ['HK$30 m to HK$50 m', '1.5%', 'HK$600k - HK$750k'],
                        ['Over HK$50 m', '1%', 'over HK$750k'],
                    ].map(([range, pct, matRange]) => (
                        <TableRow key={range}>
                            <TableCell>{range}</TableCell>
                            <TableCell align="right">{pct}</TableCell>
                            <TableCell align="right">{matRange}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        <Typography variant="subtitle2" fontWeight={600} sx={{mb: 1}}>
            Application of materiality ranges to business
        </Typography>
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell rowSpan={2}/>
                        <TableCell align="center" colSpan={3}>Initial assessment</TableCell>
                        <TableCell align="center" colSpan={3}>Final assessment</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell align="right">Percentage %</TableCell>
                        <TableCell align="right">Anticipated results</TableCell>
                        <TableCell align="right">Materiality level</TableCell>
                        <TableCell align="right">Percentage %</TableCell>
                        <TableCell align="right">Final results</TableCell>
                        <TableCell align="right">Materiality level</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <MaterialityRow
                        label="Turnover"
                        initPct={data.turnPct}
                        initResult={data.turnAntRes}
                        initLevel={data.turnMatLevel}
                        finPct={data.turnFinPct}
                        finResult={data.turnFinRes}
                        finLevel={data.turnFinMatLevel}
                    />
                    <MaterialityRow
                        label="Gross assets"
                        initPct={data.grossPct}
                        initResult={data.grossAntRes}
                        initLevel={data.grossMatLevel}
                        finPct={data.grrossPercentage}
                        finResult={data.grossFinRes}
                        finLevel={data.grossFinMatLevel}
                    />
                    <TableRow>
                        <TableCell>Profit/(loss) before tax</TableCell>
                        <TableCell/>
                        <TableCell align="right">{formatAmount(data.proAntRes)}</TableCell>
                        <TableCell/>
                        <TableCell/>
                        <TableCell align="right">{formatAmount(data.proFinRes)}</TableCell>
                        <TableCell/>
                    </TableRow>
                    <TableRow>
                        <TableCell>Adjustments for unusual items</TableCell>
                        <TableCell/>
                        <TableCell align="right">{formatAmount(data.adjusmentAntRes)}</TableCell>
                        <TableCell/>
                        <TableCell/>
                        <TableCell align="right">{formatAmount(data.adjusmentFinRes)}</TableCell>
                        <TableCell/>
                    </TableRow>
                    <TableRow sx={{background: 'action.hover'}}>
                        <TableCell sx={{fontWeight: 600}}>Adjusted profit/(loss)</TableCell>
                        <TableCell/>
                        <TableCell align="right">{formatAmount(data.adjAnticatedResults)}</TableCell>
                        <TableCell align="right">{formatAmount(data.adjMatelityLevel)}</TableCell>
                        <TableCell/>
                        <TableCell align="right">{formatAmount(data.adjFinalResults)}</TableCell>
                        <TableCell align="right">{formatAmount(data.adjMatelityLevel2)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>

        <Divider sx={{my: 3}}/>

        <Grid container spacing={2}>
            <Grid size={{xs: 12, md: 6}}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Overall materiality — This year
                </Typography>
                <HeaderField label="Initial:" value={formatAmount(data.thisYear)}/>
                <HeaderField label="Final (max):" value={formatAmount(Math.max(
                    Number(data.turnFinMatLevel ?? 0),
                    Number(data.grossFinMatLevel ?? 0),
                ))}/>
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Performance materiality (pri4)
                </Typography>
                <HeaderField label="Calculated:" value={formatAmount(data.pri4)}/>
            </Grid>
        </Grid>
    </Box>
);

export default C71DeterminingMateriality;
